import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import SafeImage from '../../../components/SafeImage';

const MAINTENANCE_MSG = 'Sorry, this game is under maintenance. Please try another game.';

export default function AddGamePanel({ games, accounts, close, gameCreations, onStartCreate }) {
  const [selectedId, setSelectedId] = useState(null);
  const selectedCardRef = useRef(null);

  useEffect(() => {
    if (!selectedId || !selectedCardRef.current) return;
    const frame = requestAnimationFrame(() => {
      selectedCardRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedId]);

  const statuses = useMemo(() => {
    const out = {};
    Object.entries(gameCreations || {}).forEach(([gid, v]) => { out[gid] = v.status; });
    return out;
  }, [gameCreations]);

  const errors = useMemo(() => {
    const out = {};
    Object.entries(gameCreations || {}).forEach(([gid, v]) => { if (v.error) out[gid] = v.error; });
    return out;
  }, [gameCreations]);

  const ownedKeys = useMemo(() => {
    const s = new Set();
    accounts.forEach(a => { if (a.game_id) s.add(a.game_id); if (a.game_code) s.add(a.game_code); });
    return s;
  }, [accounts]);

  const createGame = (game) => {
    if (!game || game.catalog_only || statuses[game.id] === 'creating' || statuses[game.id] === 'success') return;
    if (onStartCreate) {
      try { onStartCreate(game); } catch { /* noop */ }
    }
    if (close) close();
    setSelectedId(null);
  };

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto pr-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {games.length === 0 ? (
          <p className="text-center py-8 text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>No games available right now.</p>
        ) : (
          <div className={`grid grid-cols-2 gap-2.5 pt-1.5 sm:grid-cols-3 md:grid-cols-4 ${selectedId ? 'pb-32' : 'pb-1.5'}`} data-testid="available-games-icon-grid">
            {games.map(g => {
              const isOwned = ownedKeys.has(g.id) || ownedKeys.has(g.code);
              const status = statuses[g.id];
              const isCreating = status === 'creating';
              const isSuccess = status === 'success';
              const isError = status === 'error';
              const isSelected = selectedId === g.id;
              const rawImg = g.logo_image_url || g.logo_url || g.image_url || g.image || null;
              const img = rawImg
                ? (rawImg.startsWith('http') ? rawImg : `${process.env.REACT_APP_BACKEND_URL || ''}${rawImg}`)
                : 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=300&fit=crop';

              return (
                <div
                  key={g.id}
                  ref={isSelected ? selectedCardRef : null}
                  className="relative overflow-hidden rounded-[18px] border transition"
                  style={{
                    opacity: isOwned ? 0.54 : 1,
                    borderColor: isSelected ? 'rgba(0,77,97,0.62)' : 'rgba(255,255,255,0.12)',
                    background: isSelected ? 'rgba(0,77,97,0.08)' : 'rgba(255,255,255,0.055)',
                    boxShadow: isSelected ? '0 0 28px rgba(0,77,97,0.14)' : 'none',
                  }}
                  data-testid={`game-card-${g.id}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (g.catalog_only || isOwned || isCreating || isSuccess) return;
                      setSelectedId(prev => (prev === g.id ? null : g.id));
                    }}
                    disabled={g.catalog_only || isOwned || isCreating || isSuccess}
                    className="block w-full p-2 text-left transition active:scale-[0.98]"
                    aria-pressed={isSelected || isSuccess}
                    aria-busy={isCreating}
                  >
                    <div className="relative aspect-[1.12/1] overflow-hidden rounded-[15px] bg-black/50">
                      <SafeImage
                        src={img}
                        alt={g.name}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        fallback={<div className="flex h-full w-full items-center justify-center bg-black text-3xl font-black text-white">{g.name?.charAt(0) || '?'}</div>}
                      />
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/82 to-transparent" />
                      {(isOwned || isSuccess || isCreating || isError) && (
                        <span
                          className="absolute right-2 top-2 grid min-h-7 min-w-7 place-items-center rounded-full px-2 text-[10px] font-black uppercase tracking-wide"
                          style={{
                            background: isSuccess ? '#004D61' : isError ? '#EF4444' : isCreating ? '#FACC15' : 'rgba(255,255,255,0.86)',
                            color: isSuccess ? '#031A2A' : isError ? '#FFFFFF' : '#050B12',
                          }}
                        >
                          {isSuccess ? <Check className="h-4 w-4" strokeWidth={4} /> : isCreating ? '...' : isOwned ? 'Added' : '!' }
                        </span>
                      )}
                    </div>

                    <div className="px-1 pb-1 pt-1.5">
                      <p className="display truncate text-xs font-extrabold text-white sm:text-sm">{g.name}</p>
                      {g.catalog_only && <p className="mt-1 text-xs text-white/80">Coming soon · provider setup pending</p>}
                      {isOwned && !status && <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-emerald-200/85">Already added</p>}
                      {isCreating && <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-yellow-200">Creating account</p>}
                      {isSuccess && <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-emerald-200">Account created</p>}
                      {isError && <p className="mt-1 text-[11px] font-semibold text-red-300">{errors[g.id] || MAINTENANCE_MSG}</p>}
                    </div>
                  </button>

                  {isSelected && !isOwned && !isCreating && !isSuccess && (
                    <div className="grid grid-cols-2 gap-1.5 px-2 pb-2" data-testid={`game-card-actions-${g.id}`}>
                      <button
                        type="button"
                        onClick={() => createGame(g)}
                        className="rounded-full px-2 py-1.5 text-[10px] font-black text-[#031A2A] transition active:scale-95"
                        style={{ background: 'linear-gradient(135deg, #004D61, #075F75)', boxShadow: '0 8px 18px rgba(0,77,97,0.22)' }}
                        data-testid={`game-card-create-${g.id}`}
                      >
                        Create account
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedId(null)}
                        className="rounded-full border px-2 py-1.5 text-[10px] font-black text-white transition active:scale-95"
                        style={{ borderColor: 'rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)' }}
                        data-testid={`game-card-cancel-${g.id}`}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
